/**
 * useEkycApplication Hook
 * Manages eKYC application state and operations for streamers
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, ApiError, EKYCStatus, EKYCStatusResponse, EKYCApplicationResponse } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';

export type ApplicationState = 'idle' | 'loading' | 'redirecting' | 'processing' | 'completed' | 'error';

export interface UseEkycApplicationReturn {
    // Status
    ekycStatus: EKYCStatus | null;
    currentApplication: EKYCApplicationResponse | null;
    canReapply: boolean;
    applicationState: ApplicationState;
    error: string | null;
    isLoading: boolean;

    // Consent state
    termsAgreed: boolean;
    privacyAgreed: boolean;
    setTermsAgreed: (agreed: boolean) => void;
    setPrivacyAgreed: (agreed: boolean) => void;
    canProceed: boolean;

    // Actions
    refreshStatus: () => Promise<void>;
    startApplication: () => Promise<void>;
    resetError: () => void;
}

export function useEkycApplication(): UseEkycApplicationReturn {
    const { isStreamer } = useAuth();

    // Status state
    const [ekycStatus, setEkycStatus] = useState<EKYCStatus | null>(null);
    const [currentApplication, setCurrentApplication] = useState<EKYCApplicationResponse | null>(null);
    const [canReapply, setCanReapply] = useState(false);
    const [applicationState, setApplicationState] = useState<ApplicationState>('idle');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Consent state
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [privacyAgreed, setPrivacyAgreed] = useState(false);

    // Check if user can proceed with application
    const canProceed = termsAgreed && privacyAgreed && applicationState === 'idle';

    // Fetch current eKYC status
    const refreshStatus = useCallback(async () => {
        // Skip for non-streamers
        if (!isStreamer()) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const response: EKYCStatusResponse = await apiClient.getMyEKYCStatus();
            const status = response.status || (response.is_verified ? 'APPROVED' : 'NOT_APPLIED');
            setEkycStatus(status);
            setCanReapply(response.can_apply);
            setCurrentApplication(null);

            // Update application state based on status
            if (status === 'APPROVED') {
                setApplicationState('completed');
            } else if (status === 'SUBMITTED' || status === 'API_VERIFYING' || status === 'PENDING_REVIEW') {
                setApplicationState('processing');
            } else {
                setApplicationState('idle');
            }
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.detail);
            } else {
                setError('eKYCステータスの取得に失敗しました');
            }
            setApplicationState('error');
        } finally {
            setIsLoading(false);
        }
    }, [isStreamer]);

    // Start new application
    const startApplication = useCallback(async () => {
        if (!isStreamer()) {
            throw new Error('eKYC申請は配信者のみ利用可能です');
        }

        if (!canProceed) {
            throw new Error('利用規約とプライバシーポリシーへの同意が必要です');
        }

        try {
            setApplicationState('loading');
            setError(null);

            const response = await apiClient.startEKYC();
            if (!response.provider_redirect_url) {
                throw new Error('本人確認画面のURLを取得できませんでした');
            }

            setCurrentApplication(response);
            setEkycStatus(response.status);
            setApplicationState('redirecting');
            window.location.assign(response.provider_redirect_url);
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.detail);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('eKYC申請の開始に失敗しました');
            }
            setApplicationState('error');
            throw err;
        }
    }, [isStreamer, canProceed, termsAgreed, privacyAgreed]);

    // Reset error state
    const resetError = useCallback(() => {
        setError(null);
        if (applicationState === 'error') {
            setApplicationState('idle');
        }
    }, [applicationState]);

    // Load initial status
    useEffect(() => {
        refreshStatus();
    }, [refreshStatus]);

    return {
        // Status
        ekycStatus,
        currentApplication,
        canReapply,
        applicationState,
        error,
        isLoading,

        // Consent state
        termsAgreed,
        privacyAgreed,
        setTermsAgreed,
        setPrivacyAgreed,
        canProceed,

        // Actions
        refreshStatus,
        startApplication,
        resetError,
    };
}
